/**
 * Events page. content/pages/events.md is one markdown table, a row per
 * event, with ISO dates in its Dates column. The build ships it as a plain
 * table, which reads fine without JavaScript. This module sorts it against
 * the reader's own date, so an event moves from Upcoming to Past on the day
 * it ends, not on the next build.
 *
 * Dates cell forms: "2026-11-14", "2026-11-14 to 2026-11-16", "2026-11"
 * (month known, day not), "2026-09 to 2026-12", "2025" (only the year known)
 * and anything else ("TBA"), which sorts after every dated upcoming event.
 */

import { flagFor } from "./flags";

export type Status = "upcoming" | "live" | "past";

interface CalendarRow {
  el: HTMLTableRowElement;
  start: Date | null;
  end: Date | null;
  format: string;
  status: Status;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})$/;
const ISO_YEAR = /^(\d{4})$/;

export const dayFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const monthFmt = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric" });

export function today(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

type Precision = "day" | "month" | "year";

/** One end of a range: a day, or a whole month or year (its first or last day). */
function parsePart(text: string, edge: "start" | "end"): { date: Date; precision: Precision } | null {
  let m = ISO_DAY.exec(text);
  if (m) return { date: new Date(+m[1], +m[2] - 1, +m[3]), precision: "day" };
  m = ISO_MONTH.exec(text);
  if (m) {
    const date = edge === "start" ? new Date(+m[1], +m[2] - 1, 1) : new Date(+m[1], +m[2], 0);
    return { date, precision: "month" };
  }
  m = ISO_YEAR.exec(text);
  if (m) {
    const date = edge === "start" ? new Date(+m[1], 0, 1) : new Date(+m[1], 11, 31);
    return { date, precision: "year" };
  }
  return null;
}

export function parseDates(cell: string): { start: Date; end: Date; label: string } | null {
  const [a, b] = cell.split(/\s+to\s+/).map((s) => s.trim());
  const start = parsePart(a, "start");
  if (!start) return null;
  const end = b ? parsePart(b, "end") : parsePart(a, "end");
  if (!end) return null;

  let label: string;
  if (start.precision === "year") {
    label = String(start.date.getFullYear());
  } else if (start.precision === "month") {
    const sameYear = start.date.getFullYear() === end.date.getFullYear();
    label =
      !b || start.date.getMonth() === end.date.getMonth()
        ? monthFmt.format(start.date)
        : `${start.date.toLocaleDateString("en-GB", sameYear ? { month: "short" } : { month: "short", year: "numeric" })} to ${monthFmt.format(end.date)}`;
  } else if (!b || +start.date === +end.date) {
    label = dayFmt.format(start.date);
  } else {
    const sameYear = start.date.getFullYear() === end.date.getFullYear();
    const sameMonth = sameYear && start.date.getMonth() === end.date.getMonth();
    const from = sameMonth
      ? String(start.date.getDate())
      : start.date.toLocaleDateString("en-GB", sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
    label = `${from} to ${dayFmt.format(end.date)}`;
  }
  return { start: start.date, end: end.date, label };
}

export function columnIndex(table: HTMLTableElement, name: string): number {
  const heads = Array.from(table.querySelectorAll("thead th"));
  return heads.findIndex((th) => th.textContent?.trim().toLowerCase() === name);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Gives each row a stable id (data-event-id) from the event name, adding the
 * year when the name has none, so "NHRL Open Qualifier, round 1" in 2025 and
 * in 2026 differ. Ids follow the markdown's row order, so a table fetched from
 * /events/ and the one on the page agree. Run it before anything reorders rows.
 */
export function tagEvents(table: HTMLTableElement): void {
  const dateCol = columnIndex(table, "dates");
  const eventCol = columnIndex(table, "event");
  if (dateCol < 0 || eventCol < 0) return;
  const seen = new Map<string, number>();
  for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
    if (row.dataset.eventId) continue;
    const raw = row.cells[dateCol]?.textContent?.trim() ?? "";
    const name = row.cells[eventCol]?.textContent?.trim() ?? "";
    row.dataset.dates = raw;
    let id = slugify(name);
    const year = /^\d{4}/.exec(raw)?.[0];
    if (year && !/(19|20)\d{2}/.test(name)) id += `-${year}`;
    const n = (seen.get(id) ?? 0) + 1;
    seen.set(id, n);
    row.dataset.eventId = n > 1 ? `${id}-${n}` : id;
  }
}

/** Reads every row of a calendar table and works out where it stands today. */
export function readCalendar(table: HTMLTableElement): CalendarRow[] {
  tagEvents(table);
  const dateCol = columnIndex(table, "dates");
  const formatCol = columnIndex(table, "format");
  if (dateCol < 0) return [];
  const now = today();

  return Array.from(table.tBodies[0]?.rows ?? []).map((el) => {
    const cell = el.cells[dateCol];
    const parsed = parseDates(el.dataset.dates ?? "");
    let status: Status = "upcoming";
    if (parsed) {
      if (parsed.end < now) status = "past";
      else if (parsed.start <= now) status = "live";
    }
    if (cell && parsed && !cell.querySelector("time")) {
      const time = document.createElement("time");
      time.dateTime = cell.textContent!.trim().split(/\s+to\s+/)[0];
      time.textContent = parsed.label;
      cell.replaceChildren(time);
      if (status === "live") {
        const badge = document.createElement("span");
        badge.className = "badge badge--live";
        badge.textContent = "On now";
        cell.append(" ", badge);
      }
    }
    return {
      el,
      start: parsed?.start ?? null,
      end: parsed?.end ?? null,
      format: el.cells[formatCol]?.textContent?.trim().toLowerCase() ?? "",
      status,
    };
  });
}

/** Upcoming soonest first with undated events last; past most recent first. */
function sortRows(rows: CalendarRow[]): CalendarRow[] {
  const upcoming = rows
    .filter((r) => r.status !== "past")
    .sort((x, y) => (x.start ? +x.start : Infinity) - (y.start ? +y.start : Infinity));
  const past = rows.filter((r) => r.status === "past").sort((x, y) => +y.end! - +x.end!);
  return [...upcoming, ...past];
}

function button(label: string, pressed: boolean): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "filter";
  b.textContent = label;
  b.setAttribute("aria-pressed", String(pressed));
  return b;
}

/** How far back "Recent" reaches. */
const RECENT_DAYS = 90;

type View = "upcoming" | "recent" | "all";

/**
 * Splits the page's one table into an upcoming table and a collapsed past
 * table, and puts the filters above them. Upcoming is the default view, so a
 * reader lands on what is on from today onwards.
 */
export function initCalendar(): void {
  const root = document.querySelector<HTMLElement>("[data-calendar]");
  const upcomingTable = root?.querySelector<HTMLTableElement>("table");
  if (!root || !upcomingTable || !upcomingTable.tBodies[0]) return;
  // apply() is hoisted, so it loses the narrowing above; give it a non-null binding.
  const table: HTMLTableElement = upcomingTable;

  const rows = sortRows(readCalendar(upcomingTable));
  const upcoming = rows.filter((r) => r.status !== "past");
  const past = rows.filter((r) => r.status === "past");
  const recentFrom = today();
  recentFrom.setDate(recentFrom.getDate() - RECENT_DAYS);
  const isRecent = (r: CalendarRow) => r.end !== null && r.end >= recentFrom;

  // Upcoming rows stay in the original table; past rows move to a copy of it
  // inside a disclosure, newest first.
  upcomingTable.classList.add("calendar");
  upcomingTable.tBodies[0].replaceChildren(...upcoming.map((r) => r.el));
  const pastTable = upcomingTable.cloneNode(false) as HTMLTableElement;
  pastTable.append(upcomingTable.tHead!.cloneNode(true), document.createElement("tbody"));
  pastTable.tBodies[0].append(...past.map((r) => r.el));

  const upcomingHead = document.createElement("h2");
  upcomingHead.textContent = "Upcoming";
  const pastBlock = document.createElement("details");
  pastBlock.className = "calendar-past";
  const pastSummary = document.createElement("summary");
  pastBlock.append(pastSummary, pastTable);
  upcomingTable.before(upcomingHead);
  upcomingTable.after(pastBlock);

  let view: View = "upcoming";
  let format = "all";

  const controls = document.createElement("div");
  controls.className = "calendar-controls";
  const whenGroup = document.createElement("div");
  whenGroup.className = "filters";
  whenGroup.setAttribute("aria-label", "When");
  const formatGroup = document.createElement("div");
  formatGroup.className = "filters";
  formatGroup.setAttribute("aria-label", "Format");
  const summary = document.createElement("p");
  summary.className = "calendar-summary muted";
  summary.setAttribute("aria-live", "polite");
  controls.append(whenGroup, formatGroup, summary);
  upcomingHead.before(controls);

  const views: { key: View; label: string }[] = [
    { key: "upcoming", label: `Upcoming (${upcoming.length})` },
    { key: "recent", label: `Recent (${past.filter(isRecent).length})` },
    { key: "all", label: `All (${rows.length})` },
  ];
  const whenButtons = views.map(({ key, label }) => {
    const b = button(label, key === view);
    b.addEventListener("click", () => {
      view = key;
      whenButtons.forEach((other) => other.setAttribute("aria-pressed", String(other === b)));
      apply();
    });
    return b;
  });
  whenGroup.append(...whenButtons);

  const formats = ["all", ...Array.from(new Set(rows.map((r) => r.format).filter(Boolean))).sort()];
  const formatButtons = formats.map((key) => {
    const b = button(key === "all" ? "Every format" : key, key === format);
    b.addEventListener("click", () => {
      format = key;
      formatButtons.forEach((other) => other.setAttribute("aria-pressed", String(other === b)));
      apply();
    });
    return b;
  });
  formatGroup.append(...formatButtons);

  function apply(): void {
    const formatOk = (r: CalendarRow) => format === "all" || r.format === format;
    let upcomingShown = 0;
    let pastShown = 0;
    for (const r of upcoming) {
      r.el.hidden = view === "recent" || !formatOk(r);
      if (!r.el.hidden) upcomingShown++;
    }
    for (const r of past) {
      r.el.hidden = view === "upcoming" || (view === "recent" && !isRecent(r)) || !formatOk(r);
      if (!r.el.hidden) pastShown++;
    }

    upcomingHead.hidden = table.hidden = view === "recent";
    pastBlock.hidden = view === "upcoming";
    // Recent is all past events, so it opens them; All keeps them folded.
    pastBlock.open = view === "recent";
    pastSummary.textContent =
      view === "recent" ? `Ended in the last ${RECENT_DAYS} days (${pastShown})` : `Past events (${pastShown})`;

    const day = dayFmt.format(today());
    const shown = upcomingShown + pastShown;
    summary.textContent = shown
      ? `${shown} event${shown === 1 ? "" : "s"}, as of today, ${day}.`
      : `Nothing listed for this view, as of ${day}.`;
  }
  apply();
}

let fetchedTable: Promise<HTMLTableElement | null> | null = null;

/**
 * The events table from /events/, fetched and parsed once per page however
 * many callers ask (the homepage strip and the panels both do). Null when the
 * fetch fails.
 */
export function fetchEventsTable(url = "/events/"): Promise<HTMLTableElement | null> {
  fetchedTable ??= fetch(url)
    .then((res) => (res.ok ? res.text() : ""))
    .then((html) => new DOMParser().parseFromString(html, "text/html").querySelector<HTMLTableElement>("[data-calendar] table"))
    .catch(() => null);
  return fetchedTable;
}

/**
 * Homepage strip: fetches the events page and shows the next few events.
 * Without JavaScript, or if the fetch fails, the strip keeps its fallback
 * link to the events page.
 */
export async function initUpcoming(): Promise<void> {
  const host = document.querySelector<HTMLElement>("[data-upcoming-from]");
  if (!host) return;
  const limit = Number(host.dataset.upcomingLimit ?? "6");

  try {
    const table = await fetchEventsTable(host.dataset.upcomingFrom);
    if (!table) return;

    const next = sortRows(readCalendar(table)).filter((r) => r.status !== "past" && r.start).slice(0, limit);
    if (!next.length) return;

    const list = document.createElement("ol");
    list.className = "upcoming";
    for (const r of next) {
      const item = document.createElement("li");
      item.className = "upcoming__item";
      const cells = Array.from(r.el.cells);
      const [dates, event, fmt, where] = cells;
      const when = document.createElement("div");
      when.className = "upcoming__when";
      when.append(...Array.from(dates.childNodes).map((n) => document.importNode(n, true)));
      const what = document.createElement("div");
      what.className = "upcoming__what";
      const link = document.createElement("a");
      link.href = `#event/${r.el.dataset.eventId}`;
      link.textContent = event.textContent?.trim() ?? "";
      what.append(link);
      const meta = document.createElement("div");
      meta.className = "upcoming__meta muted";
      const place = where?.textContent?.trim() ?? "";
      const flag = flagFor(place);
      meta.append(...(flag ? [flag] : []), [fmt?.textContent?.trim(), place].filter(Boolean).join(" · "));
      item.append(when, what, meta);
      list.append(item);
    }
    host.replaceChildren(list);
  } catch {
    // Keep the fallback link.
  }
}
